import { loadGoogleMapsScript } from './loadGoogleMaps';

export interface ParsedUSAddress {
  street1?: string;
  street2?: string;
  locality?: string; // City
  region?: string; // State
  postalCode?: string; // ZIP code
  country?: string;
}

export interface PlaceDetails {
  name: string;
  placeId: string;
  formattedAddress?: string;
  phoneNumber?: string;
  addressComponents?: ParsedUSAddress;
  website?: string;
}

/**
 * Checks if Google Maps API is loaded and ready to use
 */
export const isGoogleMapsLoaded = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.google !== 'undefined' &&
    typeof window.google.maps !== 'undefined' &&
    typeof window.google.maps.places !== 'undefined'
  );
};

/**
 * Waits for Google Maps API to load, dynamically loading if needed
 * @throws Error if API fails to load within timeout period
 */
export const waitForGoogleMaps = async (): Promise<void> => {
  if (isGoogleMapsLoaded()) {
    return;
  }

  await loadGoogleMapsScript();
};

/**
 * Parses Google Places address components into a structured US address format
 */
export const parseUSResult = (
  result: google.maps.places.PlaceResult,
): ParsedUSAddress | undefined => {
  if (!result.address_components) {
    return undefined;
  }

  const address: ParsedUSAddress = {};

  result.address_components.forEach(component => {
    const types = component.types;

    if (types.includes('street_number')) {
      address.street1 = component.long_name;
    }

    if (types.includes('route')) {
      address.street1 = address.street1
        ? `${address.street1} ${component.long_name}`
        : component.long_name;
    }

    if (types.includes('subpremise')) {
      address.street2 = component.long_name;
    }

    if (types.includes('locality')) {
      address.locality = component.long_name;
    } else if (types.includes('administrative_area_level_2')) {
      address.locality = address.locality || component.long_name;
    }

    if (types.includes('administrative_area_level_1')) {
      address.region = component.short_name;
    }

    if (types.includes('postal_code')) {
      address.postalCode = component.long_name;
    }

    if (types.includes('country')) {
      address.country = component.short_name;
    }
  });

  return address;
};

/**
 * Fetches full place details from Google Places API using a place ID
 * @throws Error if placeId is invalid or API call fails
 */
export const getPlaceDetails = async (
  placeId: string,
): Promise<PlaceDetails> => {
  if (!placeId || typeof placeId !== 'string') {
    throw new Error('Place ID must be a non-empty string');
  }

  if (!isGoogleMapsLoaded()) {
    throw new Error('Google Maps API is not loaded');
  }

  return new Promise((resolve, reject) => {
    const dummyDiv = document.createElement('div');
    const placesService = new window.google.maps.places.PlacesService(dummyDiv);

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId,
      fields: [
        'name',
        'formatted_address',
        'address_components',
        'formatted_phone_number',
        'website',
        'place_id',
      ],
    };

    placesService.getDetails(request, (result, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        result
      ) {
        const addressComponents = parseUSResult(result);
        let formattedAddress = result.formatted_address;

        if (addressComponents) {
          const parts = [
            addressComponents.street1,
            addressComponents.street2,
            addressComponents.locality,
            addressComponents.region,
            addressComponents.postalCode,
          ]
            .filter(Boolean)
            .join(', ');
          if (parts) {
            formattedAddress = parts;
          }
        }

        resolve({
          name: result.name || '',
          placeId: result.place_id || placeId,
          formattedAddress,
          phoneNumber: result.formatted_phone_number,
          addressComponents,
          website: result.website,
        });
      } else {
        reject(
          new Error(
            `Failed to fetch place details: ${status || 'UNKNOWN_ERROR'}`,
          ),
        );
      }
    });
  });
};
